import React from "react";
import { withRouter, Link } from "react-router-dom";
import { injectIntl } from "react-intl";
import userPlaceholderImg from "../../img/placeholders/user-placeholder.png";

const UserNav = ({
  list,
  popoverStatsFor,
  intl,
  onItemClick,
  clickable,
  selectedId,
  useProjectStats,
  getLinkUrl,
}) => {
  const handleReviewerClick = reviewer => {
    if (clickable && onItemClick) {
      onItemClick(reviewer);
    }
  };

  const isSelected = reviewer => {
    const profileId = reviewer.profile?.id || reviewer.profile_id;
    return selectedId && profileId === selectedId;
  };

  const getStats = reviewer => {
    if (useProjectStats && reviewer.project_stats) {
      return { done: reviewer.project_stats.done, total: reviewer.project_stats.total };
    }
    return { done: reviewer.done || 0, total: reviewer.total || 0 };
  };

  const handleKeyDown = (e, reviewer) => {
    if (clickable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleReviewerClick(reviewer);
    }
  };

  const getProgressPercent = reviewer => {
    const stats = getStats(reviewer);
    if (!stats.total) return 0;
    return Math.round((stats.done / stats.total) * 100);
  };

  const getStatusClass = reviewer => {
    const percent = getProgressPercent(reviewer);
    if (percent === 100) return "user-nav__status--complete";
    if (percent >= 50) return "user-nav__status--progress";
    return "user-nav__status--pending";
  };

  const sortedList = [...list].sort((a, b) => {
    const nameA = (a.user.name || a.user.email || "").toLowerCase();
    const nameB = (b.user.name || b.user.email || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <ul className="user-nav" role="list" aria-label={intl.formatMessage({ id: "reviewerList" })}>
      {sortedList.map(reviewer => {
        const stats = getStats(reviewer);
        const selected = isSelected(reviewer);
        const displayName = reviewer.user.name || reviewer.user.email;
        const percent = getProgressPercent(reviewer);

        const linkUrl = getLinkUrl ? getLinkUrl(reviewer) : null;

        const content = (
          <>
            <div className="user-nav__avatar-wrap">
              <img
                className="user-nav__avatar"
                src={userPlaceholderImg}
                alt=""
                aria-hidden="true"
              />
              <svg className="user-nav__ring" viewBox="0 0 36 36" aria-hidden="true">
                <circle
                  className="user-nav__ring-bg"
                  cx="18" cy="18" r="16"
                  fill="none"
                  strokeWidth="2"
                />
                <circle
                  className={`user-nav__ring-progress ${getStatusClass(reviewer)}`}
                  cx="18" cy="18" r="16"
                  fill="none"
                  strokeWidth="2"
                  strokeDasharray={`${percent} 100`}
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div className="user-nav__info">
              <div className="user-nav__row">
                <span className="user-nav__name">{reviewer.user.name || reviewer.user.email}</span>
                <span
                  className={`user-nav__badge ${getStatusClass(reviewer)}`}
                  title={intl.formatMessage({ id: useProjectStats ? "reviewerProjectStatsTooltip" : "reviewerCollectionStatsTooltip" })}
                >
                  {stats.done}/{stats.total}
                </span>
              </div>
              {reviewer.user.name && (
                <span className="user-nav__email">{reviewer.user.email}</span>
              )}
            </div>
          </>
        );

        if (linkUrl) {
          return (
            <li
              key={reviewer.user.id}
              className={`user-nav__item ${clickable ? "user-nav__item--clickable" : ""} ${selected ? "user-nav__item--selected bc1" : ""}`}
              aria-label={`${displayName}, ${stats.done} of ${stats.total} completed${reviewer.confirmed ? ", confirmed" : ""}`}
            >
              <Link
                to={linkUrl}
                className="user-nav__link"
                aria-selected={selected}
              >
                {content}
              </Link>
            </li>
          );
        }

        return (
          <li
            key={reviewer.user.id}
            className={`user-nav__item ${clickable ? "user-nav__item--clickable" : ""} ${selected ? "user-nav__item--selected bc1" : ""}`}
            onClick={() => handleReviewerClick(reviewer)}
            onKeyDown={e => handleKeyDown(e, reviewer)}
            role={clickable ? "button" : "listitem"}
            tabIndex={clickable ? 0 : undefined}
            aria-selected={clickable ? selected : undefined}
            aria-label={`${displayName}, ${stats.done} of ${stats.total} completed${reviewer.confirmed ? ", confirmed" : ""}`}
          >
            {content}
          </li>
        );
      })}
    </ul>
  );
};

export default injectIntl(withRouter(UserNav));
